import React, { FC } from 'react';
import {
  Box,
  Container,
  Grid,
  Tab,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import SwipeableViews from 'react-swipeable-views';
import {
  tabStep1Image,
  tabStep2Image,
  tabStep3Image,
  tabStep4Image,
  tabStep5Image,
} from 'assets/images/led-landing';
import useI18n from "evcharging/hooks/useI18n";

interface TabInfo {
  title: string;
  image: string;
  description: string;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      padding: theme.spacing(14, 0),
    },
    tabHeaderWrapper: {
      justifyContent: 'space-between',
    },
    tabHeaderElement: {
      color: theme.palette.secondary.main,
      fontSize: 18,
      fontWeight: 400,
      textTransform: 'none',
      padding: theme.spacing(0, 6, 3.6),
      minHeight: 54,
      whiteSpace: 'pre-line',
    },
    indicator: {
      backgroundColor: theme.palette.primary.main,
    },
    number: {
      backgroundColor: '#FAFAFA',
      border: `1px solid #EEEEEE`,
      borderRadius: theme.spacing(2),
      width: 48,
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing(4),
    },
    tabContentWrapper: {
      padding: theme.spacing(10, 0),
    },
    image: {
      width: '100%',
      borderRadius: theme.spacing(2),
      [theme.breakpoints.up('sm')]: {
        borderRadius: theme.spacing(6),
      },
    },
    text: {
      marginTop: theme.spacing(4),
      color: theme.palette.common.black,
      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(6),
      },
    },
  };
});

const HeaderLedSection: FC = () => {
  const { t } = useI18n();
  const classes = useStyles();
  const tabsInfo: Array<TabInfo> = [
    {
      title: t('landing.area.tab1'),
      description: t('landing.area.tab1-description'),
      image: tabStep1Image,
    },
    {
      title: t('landing.area.tab2'),
      description: t('landing.area.tab2-description'),
      image: tabStep5Image,
    },
    {
      title: t('landing.area.tab3'),
      description: t('landing.area.tab3-description'),
      image: tabStep2Image,
    },
    {
      title: t('landing.area.tab4'),
      description: t('landing.area.tab4-description'),
      image: tabStep3Image,
    },
    {
      title: t('landing.area.tab5'),
      description: t('landing.area.tab5-description'),
      image: tabStep4Image,
    },
  ];
  const isSmallUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
  const [tabActive, setTabActive] = React.useState(0);
  const handleChange = (event: React.ChangeEvent, newValue: number) => {
    setTabActive(newValue);
  };

  return (
    <Box className={classes.root}>
      <Container maxWidth={false} fixed>
        <Box mb={isSmallUp ? 14 : 6}>
          <Typography variant="h2" align="center">
            {t('landing.area')}
          </Typography>
        </Box>
        <Box>
          <Tabs
            value={tabActive}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="off"
            aria-label="scrollable prevent tabs example"
            classes={{ flexContainer: classes.tabHeaderWrapper, indicator: classes.indicator }}
          >
            {tabsInfo.map((tabHeader, index) => (
              <Tab
                icon={
                  <Box className={classes.number}>
                    <Typography variant="h4" color="primary">
                      {index + 1}
                    </Typography>
                  </Box>
                }
                classes={{ wrapper: classes.tabHeaderElement }}
                aria-label={tabHeader.title}
                id={`scrollable-prevent-tab-${index}`}
                aria-controls={`scrollable-prevent-tabpanel-${index}`}
                label={tabHeader.title}
                key={index}
              />
            ))}
          </Tabs>
          <Grid container justifyContent="center">
            <Grid item lg={10} xs={12}>
              <SwipeableViews
                axis="x"
                index={tabActive}
                onChangeIndex={handleChange}
                containerStyle={{
                  transition: 'transform 0.35s cubic-bezier(0.15, 0.3, 0.25, 1) 0s',
                }}
              >
                {tabsInfo.map((tab, index) => (
                  <div
                    role="tabpanel"
                    hidden={tabActive !== index}
                    id={`scrollable-prevent-tabpanel-${index}`}
                    aria-labelledby={`scrollable-prevent-tab-${index}`}
                    key={index}
                  >
                    <Box className={classes.tabContentWrapper}>
                      <img
                        src={tab.image}
                        alt={tab.title}
                        title={tab.title}
                        className={classes.image}
                      />
                      <Typography className={classes.text}>{tab.description}</Typography>
                    </Box>
                  </div>
                ))}
              </SwipeableViews>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default HeaderLedSection;
